/**
 * MEVIA - Employee Action Plan View
 * View for employees to see their assigned action plans, track progress via subtasks
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import {
  ArrowLeft,
  User,
  Calendar,
  CheckCircle,
  Clock,
  AlertCircle,
  FileText,
  MessageSquare,
  Send,
  Target,
  BookOpen,
  ChevronRight,
  RefreshCw,
  ClipboardList,
  Sparkles,
  CheckSquare,
  DollarSign
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import logger from '../utils/logger';

// Status configuration
const STATUS_CONFIG = {
  draft: { es: 'Borrador', en: 'Draft', color: 'bg-gray-100 text-gray-800', icon: FileText },
  active: { es: 'Activo', en: 'Active', color: 'bg-blue-100 text-blue-800', icon: Clock },
  pending_validation: { es: 'Por Validar', en: 'Pending Validation', color: 'bg-amber-100 text-amber-800', icon: AlertCircle },
  validated: { es: 'Validado', en: 'Validated', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  closed: { es: 'Cerrado', en: 'Closed', color: 'bg-purple-100 text-purple-800', icon: CheckCircle }
};

const PRIORITY_CONFIG = {
  high: { es: 'Alta', en: 'High', color: 'bg-red-100 text-red-700 border-red-200' },
  medium: { es: 'Media', en: 'Medium', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  low: { es: 'Baja', en: 'Low', color: 'bg-green-100 text-green-700 border-green-200' }
};

const PROGRESS_STATUS = {
  not_started: { es: 'Sin Iniciar', en: 'Not Started', color: 'text-gray-500', bg: 'bg-gray-100' },
  in_progress: { es: 'En Progreso', en: 'In Progress', color: 'text-blue-600', bg: 'bg-blue-100' },
  completed: { es: 'Completado', en: 'Completed', color: 'text-amber-600', bg: 'bg-amber-100' },
  validated: { es: 'Validado', en: 'Validated', color: 'text-green-600', bg: 'bg-green-100' }
};

const EmployeeActionPlanView = ({ onBack }) => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);
  const [updatingItem, setUpdatingItem] = useState(null);

  // Translations
  const t = {
    title: language === 'es' ? 'Mi Plan de Desarrollo' : 'My Development Plan',
    subtitle: language === 'es' 
      ? 'Tu plan de acción personalizado basado en tus evaluaciones' 
      : 'Your personalized action plan based on your assessments',
    back: language === 'es' ? 'Volver' : 'Back',
    noPlan: language === 'es' ? 'Sin Plan Asignado' : 'No Plan Assigned',
    noPlanDesc: language === 'es' 
      ? 'Aún no tienes un plan de desarrollo asignado. Tu administrador creará uno basado en tus evaluaciones.'
      : 'You don\'t have a development plan assigned yet. Your administrator will create one based on your assessments.',
    progress: language === 'es' ? 'Progreso General' : 'Overall Progress',
    tasks: language === 'es' ? 'tareas' : 'tasks',
    subtasks: language === 'es' ? 'subtareas' : 'subtasks',
    completed: language === 'es' ? 'completadas' : 'completed',
    developmentAreas: language === 'es' ? 'Áreas de Desarrollo' : 'Development Areas',
    objective: language === 'es' ? 'Objetivo' : 'Objective',
    actions: language === 'es' ? 'Acciones a Realizar' : 'Actions to Take',
    resources: language === 'es' ? 'Recursos Recomendados' : 'Recommended Resources',
    priority: language === 'es' ? 'Prioridad' : 'Priority',
    myProgress: language === 'es' ? 'Mi Progreso' : 'My Progress',
    myChecklist: language === 'es' ? 'Mi Checklist' : 'My Checklist',
    updateProgress: language === 'es' ? 'Estado general:' : 'Overall status:',
    comments: language === 'es' ? 'Comentarios' : 'Comments',
    addComment: language === 'es' ? 'Agregar un comentario o actualización...' : 'Add a comment or update...',
    send: language === 'es' ? 'Enviar' : 'Send',
    noComments: language === 'es' ? 'Sin comentarios aún' : 'No comments yet',
    admin: language === 'es' ? 'Administrador' : 'Admin',
    you: language === 'es' ? 'Tú' : 'You',
    status: language === 'es' ? 'Estado del Plan' : 'Plan Status',
    createdAt: language === 'es' ? 'Creado el' : 'Created on',
    waitingValidation: language === 'es' 
      ? 'Este plan está pendiente de validación por tu administrador.'
      : 'This plan is pending validation by your administrator.',
    planValidated: language === 'es'
      ? '¡Felicidades! Tu plan ha sido validado exitosamente.'
      : 'Congratulations! Your plan has been successfully validated.',
    tips: language === 'es' ? 'Consejos' : 'Tips',
    tip1: language === 'es' 
      ? 'Marca las subtareas completadas para actualizar tu progreso automáticamente.'
      : 'Check off completed subtasks to automatically update your progress.',
    tip2: language === 'es'
      ? 'Usa los comentarios para comunicar avances, dudas o necesidades de apoyo.'
      : 'Use comments to communicate progress, questions, or support needs.',
    tip3: language === 'es'
      ? 'Cuando completes todas las subtareas de un área, el progreso se reflejará automáticamente.'
      : 'When you complete all subtasks in an area, progress will be reflected automatically.',
    progressUpdated: language === 'es' ? 'Progreso actualizado' : 'Progress updated',
    subtaskUpdated: language === 'es' ? 'Subtarea actualizada' : 'Subtask updated',
    commentAdded: language === 'es' ? 'Comentario agregado' : 'Comment added',
    error: language === 'es' ? 'Error al procesar la solicitud' : 'Error processing request',
    requiresBudget: language === 'es' ? 'Requiere presupuesto' : 'Requires budget'
  };

  const loadMyPlan = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/action-plans/my-plan');
      setPlan(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        setPlan(null);
      } else {
        logger.error('Error loading plan:', error);
        toast.error(t.error);
      }
    } finally {
      setLoading(false);
    }
  }, [t.error]);

  useEffect(() => {
    loadMyPlan();
  }, [loadMyPlan]);

  const handleUpdateProgress = async (itemId, newStatus) => {
    try {
      setUpdatingItem(itemId);
      const progressPercentage = newStatus === 'completed' ? 100 : newStatus === 'in_progress' ? 50 : 0;
      
      await axios.put(`/action-plans/${plan.id}/items/${itemId}`, {
        progress_status: newStatus,
        progress_percentage: progressPercentage
      });
      
      toast.success(t.progressUpdated);
      loadMyPlan();
    } catch (error) {
      logger.error('Error updating progress:', error);
      toast.error(t.error);
    } finally {
      setUpdatingItem(null);
    }
  };

  const handleToggleSubtask = async (itemId, subtaskId, currentValue) => {
    try {
      await axios.put(`/action-plans/${plan.id}/items/${itemId}/subtasks/${subtaskId}`, {
        is_completed: !currentValue
      });
      toast.success(t.subtaskUpdated);
      loadMyPlan();
    } catch (error) {
      logger.error('Error updating subtask:', error);
      toast.error(t.error);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    try {
      setAddingComment(true);
      await axios.post(`/action-plans/${plan.id}/comments`, {
        content: newComment
      });
      
      setNewComment('');
      toast.success(t.commentAdded);
      loadMyPlan();
    } catch (error) {
      logger.error('Error adding comment:', error);
      toast.error(t.error);
    } finally {
      setAddingComment(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat(language === 'es' ? 'es-MX' : 'en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  // No plan assigned
  if (!plan) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          {onBack && (
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t.back}
            </Button>
          )}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ClipboardList className="h-6 w-6 text-teal-600" />
              {t.title}
            </h2>
            <p className="text-sm text-gray-500">{t.subtitle}</p>
          </div>
        </div>

        <Card className="border-2 border-dashed border-gray-200">
          <CardContent className="py-12 text-center">
            <Target className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t.noPlan}</h3>
            <p className="text-gray-500 max-w-md mx-auto">{t.noPlanDesc}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const StatusIcon = STATUS_CONFIG[plan.status]?.icon || FileText;
  const canUpdateProgress = plan.status === 'active';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {onBack && (
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t.back}
            </Button>
          )}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ClipboardList className="h-6 w-6 text-teal-600" />
              {t.title}
            </h2>
            <p className="text-sm text-gray-500">{t.subtitle}</p>
          </div>
        </div>
        <Badge className={`${STATUS_CONFIG[plan.status]?.color} flex items-center gap-1 px-3 py-1`}>
          <StatusIcon className="h-4 w-4" />
          {STATUS_CONFIG[plan.status]?.[language]}
        </Badge>
      </div>

      {/* Status Messages */}
      {plan.status === 'pending_validation' && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              <p className="text-amber-800">{t.waitingValidation}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {plan.status === 'validated' && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <p className="text-green-800">{t.planValidated}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Progress Overview */}
      <Card>
        <CardContent className="py-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-teal-100 flex items-center justify-center">
                <Target className="h-6 w-6 text-teal-600" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">{t.progress}</h3>
                <p className="text-sm text-gray-500">
                  {plan.progress?.completed_subtasks || 0}/{plan.progress?.total_subtasks || 0} {t.subtasks} {t.completed}
                </p>
              </div>
            </div>
            <div className="text-3xl font-bold text-teal-600">
              {plan.progress?.percentage || 0}%
            </div>
          </div>
          <Progress value={plan.progress?.percentage || 0} className="h-4" />
          <p className="text-xs text-gray-500 mt-2">{t.createdAt}: {formatDate(plan.created_at)}</p>
        </CardContent>
      </Card>

      {/* Tips Card */}
      {canUpdateProgress && (
        <Card className="bg-gradient-to-r from-teal-50 to-cyan-50 border-teal-200">
          <CardContent className="py-4">
            <h4 className="font-medium text-teal-800 mb-2 flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              {t.tips}
            </h4>
            <ul className="text-sm text-teal-700 space-y-1">
              <li className="flex items-start gap-2">
                <ChevronRight className="h-4 w-4 mt-0.5 flex-shrink-0" />
                {t.tip1}
              </li>
              <li className="flex items-start gap-2">
                <ChevronRight className="h-4 w-4 mt-0.5 flex-shrink-0" />
                {t.tip2}
              </li>
              <li className="flex items-start gap-2">
                <ChevronRight className="h-4 w-4 mt-0.5 flex-shrink-0" />
                {t.tip3}
              </li>
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Development Areas */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-teal-600" />
            {t.developmentAreas}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion type="multiple" defaultValue={plan.items?.map(i => i.id)} className="space-y-4">
            {plan.items?.map((item, index) => {
              const completedSubtasks = item.subtasks?.filter(st => st.is_completed).length || 0;
              const totalSubtasks = item.subtasks?.length || 0;
              const subtaskProgress = totalSubtasks > 0 ? (completedSubtasks / totalSubtasks * 100) : 0;
              
              return (
                <AccordionItem 
                  key={item.id} 
                  value={item.id}
                  className="border rounded-lg px-4"
                >
                  <AccordionTrigger className="hover:no-underline py-4">
                    <div className="flex items-center gap-4 flex-1 text-left">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                        subtaskProgress === 100 ? 'bg-green-100 text-green-700' :
                        subtaskProgress > 0 ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {subtaskProgress === 100 ? (
                          <CheckCircle className="h-5 w-5" />
                        ) : (
                          index + 1
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold">{item.area}</p>
                          <Badge className={`${PRIORITY_CONFIG[item.priority]?.color} border text-xs`}>
                            {PRIORITY_CONFIG[item.priority]?.[language]}
                          </Badge>
                          {item.requires_budget && (
                            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">
                              <DollarSign className="h-3 w-3 mr-1" />
                              {t.requiresBudget}
                            </Badge>
                          )}
                        </div>
                        {totalSubtasks > 0 && (
                          <div className="flex items-center gap-2 mt-2">
                            <Progress value={subtaskProgress} className="h-2 flex-1 max-w-32" />
                            <span className="text-xs text-gray-500">{completedSubtasks}/{totalSubtasks}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-4">
                    <div className="space-y-4 pt-2">
                      {/* Objective */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm font-medium text-gray-700 mb-1">{t.objective}:</p>
                        <p className="text-gray-900">{item.objective}</p>
                      </div>

                      {/* Subtasks Checklist - Main interaction for employees */}
                      {totalSubtasks > 0 && (
                        <div className="bg-teal-50 rounded-lg p-4 border border-teal-200">
                          <p className="text-sm font-medium text-teal-800 mb-3 flex items-center gap-2">
                            <CheckSquare className="h-4 w-4" />
                            {t.myChecklist} ({completedSubtasks}/{totalSubtasks})
                          </p>
                          <div className="space-y-3">
                            {item.subtasks?.map((subtask) => (
                              <div key={subtask.id} className="flex items-start gap-3">
                                <Checkbox
                                  id={`emp-${subtask.id}`}
                                  checked={subtask.is_completed}
                                  onCheckedChange={() => handleToggleSubtask(item.id, subtask.id, subtask.is_completed)}
                                  disabled={!canUpdateProgress}
                                  className="mt-0.5 data-[state=checked]:bg-teal-600"
                                />
                                <label 
                                  htmlFor={`emp-${subtask.id}`}
                                  className={`flex-1 text-sm cursor-pointer ${
                                    subtask.is_completed 
                                      ? 'text-gray-400 line-through' 
                                      : 'text-gray-700'
                                  }`}
                                >
                                  {subtask.title}
                                </label>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">{t.actions}:</p>
                        <ul className="space-y-2">
                          {item.actions?.map((action, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm">
                              <ChevronRight className="h-4 w-4 text-teal-500 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-700">{typeof action === 'string' ? action : action?.description || JSON.stringify(action)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Resources */}
                      {item.resources?.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">{t.resources}:</p>
                          <div className="flex flex-wrap gap-2">
                            {item.resources.map((resource, i) => (
                              <Badge key={i} variant="outline" className="text-xs bg-white">
                                {resource}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Progress Status Selector (optional - main progress is via subtasks now) */}
                      {canUpdateProgress && (
                        <div className="flex items-center gap-4 pt-4 border-t">
                          <span className="text-sm font-medium text-gray-700">{t.updateProgress}</span>
                          <Select
                            value={item.progress_status}
                            onValueChange={(value) => handleUpdateProgress(item.id, value)}
                            disabled={updatingItem === item.id}
                          >
                            <SelectTrigger className="w-40">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="not_started">{PROGRESS_STATUS.not_started[language]}</SelectItem>
                              <SelectItem value="in_progress">{PROGRESS_STATUS.in_progress[language]}</SelectItem>
                              <SelectItem value="completed">{PROGRESS_STATUS.completed[language]}</SelectItem>
                            </SelectContent>
                          </Select>
                          {updatingItem === item.id && (
                            <RefreshCw className="h-4 w-4 animate-spin text-gray-400" />
                          )}
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </CardContent>
      </Card>

      {/* Comments Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-teal-600" />
            {t.comments} ({plan.comments?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Add Comment */}
          <div className="flex gap-2 mb-6">
            <Textarea
              placeholder={t.addComment}
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              className="flex-1 min-h-[80px]"
            />
            <Button 
              onClick={handleAddComment}
              disabled={!newComment.trim() || addingComment}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {addingComment ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>

          {/* Comments List */}
          {plan.comments?.length > 0 ? (
            <div className="space-y-4">
              {plan.comments.map((comment) => {
                const isMyComment = comment.author_role === 'employee';
                return (
                  <div key={comment.id} className={`rounded-lg p-4 ${isMyComment ? 'bg-teal-50 ml-4' : 'bg-gray-50 mr-4'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <span className="font-medium text-sm">
                          {isMyComment ? t.you : comment.author_name}
                        </span>
                        {!isMyComment && (
                          <Badge variant="outline" className="text-xs">
                            {t.admin}
                          </Badge>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">{formatDate(comment.created_at)}</span>
                    </div>
                    <p className="text-sm text-gray-700">{comment.content}</p>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-4">{t.noComments}</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EmployeeActionPlanView;
